/**
 * Installer Settings — org-level, six sections.
 * Phase 1: Account, Organization, Integrations, Team, Properties, Data & Billing.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Download,
  Database,
  UserPlus,
  Check,
  Building2,
  Settings,
  Link2,
  Copy,
  User,
  Building,
  Plug,
  Users,
  CreditCard,
  ArrowRight,
  Lock,
  Package,
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { useBillingPreview } from '@/hooks/useBillingPreview';
import { useOrgMembers, type OrgMember } from '@/hooks/useOrgMembers';
import { OrganizationBrandSection } from '@/components/settings/OrganizationBrandSection';
import { IntegrationsSection } from '@/components/settings/IntegrationsSection';
import { ServicePlansSection } from '@/components/settings/ServicePlansSection';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TIMEZONES } from '@/lib/timezone';

interface PendingUser {
  user_id: string;
  email: string | null;
  created_at: string;
}

export default function InstallerSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { properties, plants, organization, refreshData } = useApp();
  const { tierSummary, billingPreview, canSee, isLoading: billingLoading } = useBillingPreview();

  const [pending, setPending] = useState<PendingUser[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('homeowner');
  const [invitePropertyIds, setInvitePropertyIds] = useState<string[]>([]);
  const [invitePermRole, setInvitePermRole] = useState<string>('viewer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [backfillAllLoading, setBackfillAllLoading] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const { data: orgMembers, refetch: refetchMembers } = useOrgMembers();

  // Account: password change
  const [passwordModal, setPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Account: notification contact (organizations.notification_contact)
  const [notifEmail, setNotifEmail] = useState('');
  const [notifPhone, setNotifPhone] = useState('');
  const [notifSaving, setNotifSaving] = useState(false);

  // Account: defaults (default_timezone, default_quiet_hours)
  const [defaultTimezone, setDefaultTimezone] = useState('America/Chicago');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('22:00');
  const [quietEnd, setQuietEnd] = useState('07:00');
  const [quietTz, setQuietTz] = useState('America/Chicago');
  const [defaultsSaving, setDefaultsSaving] = useState(false);

  useEffect(() => {
    if (organization) {
      setDefaultTimezone(organization.default_timezone ?? 'America/Chicago');
      const qh = organization.default_quiet_hours as { start?: string; end?: string; timezone?: string } | null | undefined;
      if (qh) {
        setQuietHoursEnabled(true);
        setQuietStart(qh.start ?? '22:00');
        setQuietEnd(qh.end ?? '07:00');
        setQuietTz(qh.timezone ?? 'America/Chicago');
      } else {
        setQuietHoursEnabled(false);
      }
      const nc = organization.notification_contact as { email?: string; phone?: string } | null | undefined;
      setNotifEmail(nc?.email ?? '');
      setNotifPhone(nc?.phone ?? '');
    }
  }, [organization?.id, organization?.default_timezone, organization?.default_quiet_hours, organization?.notification_contact]);

  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        setPending([]);
        return;
      }
      const { data, error } = await supabase.functions.invoke('user-approval', {
        method: 'GET',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      setPending((data?.pending ?? []) as PendingUser[]);
    } catch (err) {
      console.error('Error fetching pending signups:', err);
      toast.error('Failed to load pending signups');
      setPending([]);
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchPending();
  }, [user]);

  const handleCreateInvite = async () => {
    setInviteLoading(true);
    setInviteUrl(null);
    try {
      const result = await invokeAuthedFunction<{ url?: string; error?: string }>(
        supabase,
        'create-invite',
        {
          email: inviteEmail.trim() || undefined,
          role: inviteRole,
          permissionRole: invitePermRole,
          ...(inviteRole === 'homeowner' && invitePropertyIds.length > 0
            ? { propertyIds: invitePropertyIds }
            : {}),
        },
      );
      if (result?.url) {
        setInviteUrl(result.url);
        toast.success('Invite link created');
      } else {
        toast.error(result?.error ?? 'Failed to create invite');
      }
    } catch (err) {
      console.error('Create invite error:', err);
      toast.error('Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteUrl = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => toast.success('Link copied to clipboard')).catch(() => toast.error('Could not copy'));
  };

  const handleApprove = async (userId: string) => {
    setApprovingId(userId);
    try {
      const result = await invokeAuthedFunction<{ success?: boolean; error?: string }>(
        supabase,
        'user-approval',
        { userId }
      );
      if (result?.success) {
        toast.success('User approved');
        fetchPending();
      } else {
        toast.error(result?.error ?? 'Failed to approve');
      }
    } catch (err) {
      console.error('Approve error:', err);
      toast.error('Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const handleChangeMemberRole = async (userId: string, permissionRole: string) => {
    setUpdatingMemberId(userId);
    try {
      const result = await invokeAuthedFunction<{ success?: boolean; error?: string }>(
        supabase,
        'update-member-role',
        { userId, permissionRole },
      );
      if (result?.success) {
        toast.success('Role updated');
        refetchMembers();
      } else {
        toast.error(result?.error ?? 'Failed to update role');
      }
    } catch (err) {
      console.error('Update role error:', err);
      toast.error('Failed to update role');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    setRemovingMemberId(userId);
    try {
      const result = await invokeAuthedFunction<{ success?: boolean; error?: string }>(
        supabase,
        'remove-member',
        { userId },
      );
      if (result?.success) {
        toast.success('Member removed');
        refetchMembers();
      } else {
        toast.error(result?.error ?? 'Failed to remove member');
      }
    } catch (err) {
      console.error('Remove member error:', err);
      toast.error('Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleStartBackfillForAll = async () => {
    const toBackfill = properties
      .map((prop) => ({ property: prop, plantId: plants.find((p) => p.property_id === prop.id)?.id }))
      .filter(({ plantId }) => !!plantId) as { property: (typeof properties)[0]; plantId: string }[];
    if (toBackfill.length === 0) {
      toast.error('No properties with plants to backfill');
      return;
    }
    setBackfillAllLoading(true);
    let started = 0;
    let failed = 0;
    try {
      for (const { property, plantId } of toBackfill) {
        try {
          const createResult = await invokeAuthedFunction<{ success?: boolean; jobId?: string; error?: string }>(
            supabase,
            'backfill-job',
            { action: 'create', propertyId: property.id, plantId, rangePreset: '30days' },
          );
          if (createResult?.success && createResult?.jobId) {
            await invokeAuthedFunction(supabase, 'backfill-job', { action: 'start', jobId: createResult.jobId });
            started++;
          } else {
            failed++;
          }
        } catch {
          failed++;
        }
      }
      if (started > 0) {
        toast.success(`Started 30-day backfill for ${started} propert${started === 1 ? 'y' : 'ies'}`, {
          description: failed > 0 ? `${failed} failed. Track progress in History.` : 'Track progress in History.',
        });
        refreshData();
      }
      if (failed > 0 && started === 0) toast.error('Failed to start backfill for all properties');
    } catch (err) {
      console.error('Backfill error:', err);
      toast.error('Failed to start backfill');
    } finally {
      setBackfillAllLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated');
      setPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSaveNotificationContact = async () => {
    if (!organization) return;
    setNotifSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          notification_contact: {
            email: notifEmail.trim() || null,
            phone: notifPhone.trim() || null,
            verified_email: false,
            verified_phone: false,
          },
        })
        .eq('id', organization.id);
      if (error) throw error;
      toast.success('Notification contact saved');
      refreshData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleSaveDefaults = async () => {
    if (!organization) return;
    setDefaultsSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          default_timezone: defaultTimezone,
          default_quiet_hours: quietHoursEnabled
            ? { start: quietStart, end: quietEnd, timezone: quietTz }
            : null,
        })
        .eq('id', organization.id);
      if (error) throw error;
      toast.success('Defaults saved');
      refreshData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setDefaultsSaving(false);
    }
  };

  const featureFlags = (organization?.feature_flags ?? {}) as Record<string, boolean>;
  const flagEntries = Object.entries(featureFlags);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-wt-text-primary">Settings</h1>
        <p className="text-wt-text-secondary mt-1">
          Organization and account settings. Configure properties, team, integrations, and billing.
        </p>
      </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="bg-wt-bg-secondary grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1 p-1">
          <TabsTrigger value="account" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Account</span>
          </TabsTrigger>
          <TabsTrigger value="organization" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Organization</span>
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Plug className="w-4 h-4" />
            <span className="hidden sm:inline">Integrations</span>
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team
          </TabsTrigger>
          <TabsTrigger value="properties" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Properties</span>
          </TabsTrigger>
          <TabsTrigger value="data-billing" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Data & Billing</span>
          </TabsTrigger>
          <TabsTrigger value="service-plans" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Service Plans</span>
          </TabsTrigger>
        </TabsList>

        {/* 1. Account */}
        <TabsContent value="account" className="space-y-6">
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Profile</CardTitle>
              <CardDescription>Account email and password.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email ?? ''} readOnly className="bg-wt-bg-tertiary max-w-md" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setPasswordModal(true)}>
                <Lock className="w-3.5 h-3.5 mr-1.5" />
                Change Password
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Notification Contact</CardTitle>
              <CardDescription>Default email and phone for alert delivery. Shown as unverified until verification is implemented.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={notifEmail} onChange={(e) => setNotifEmail(e.target.value)} placeholder="ops@company.com" className="max-w-md" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input type="tel" value={notifPhone} onChange={(e) => setNotifPhone(e.target.value)} placeholder="+1 234 567 8900" className="max-w-md" />
              </div>
              <Button onClick={handleSaveNotificationContact} disabled={notifSaving}>
                {notifSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Defaults</CardTitle>
              <CardDescription>Default timezone and quiet hours for the organization.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Default timezone</Label>
                <Select value={defaultTimezone} onValueChange={setDefaultTimezone}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Quiet hours</Label>
                  <p className="text-xs text-muted-foreground">Suppress non-critical notifications during this window.</p>
                </div>
                <Switch checked={quietHoursEnabled} onCheckedChange={setQuietHoursEnabled} />
              </div>
              {quietHoursEnabled && (
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div className="space-y-2">
                    <Label>Start</Label>
                    <Input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>End</Label>
                    <Input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Timezone</Label>
                    <Select value={quietTz} onValueChange={setQuietTz}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <Button onClick={handleSaveDefaults} disabled={defaultsSaving}>
                {defaultsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg">Feature Access</CardTitle>
              <CardDescription>Subscription tiers and feature flags. Read-only.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {billingLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
              ) : canSee && tierSummary && billingPreview ? (
                <>
                  <div className="text-sm">
                    <span className="text-wt-text-secondary">Monitor: {tierSummary.tiers.monitor} · Alert: {tierSummary.tiers.alert} · Predict: {tierSummary.tiers.predict}</span>
                    <span className="text-wt-text-primary font-mono ml-2">${billingPreview.monthly_total}/mo</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {flagEntries.length === 0 ? (
                      <span className="text-sm text-muted-foreground">Standard access</span>
                    ) : (
                      flagEntries.map(([key, enabled]) => (
                        <Badge key={key} variant={enabled ? 'default' : 'secondary'} className={enabled ? 'bg-wt-green-primary/20 text-wt-green-primary' : ''}>
                          {key}: {enabled ? 'On' : 'Off'}
                        </Badge>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No billing data or standard access.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. Organization */}
        <TabsContent value="organization">
          <OrganizationBrandSection />
        </TabsContent>

        {/* 3. Integrations */}
        <TabsContent value="integrations">
          <IntegrationsSection />
        </TabsContent>

        {/* 4. Team */}
        <TabsContent value="team" className="space-y-6">
          {/* Team Members */}
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members
              </CardTitle>
              <CardDescription>Members of your organization. Owners can change roles and remove members.</CardDescription>
            </CardHeader>
            <CardContent>
              {!orgMembers ? (
                <div className="flex justify-center h-[120px] items-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : orgMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No team members yet.</p>
              ) : (
                <div className="space-y-2">
                  {orgMembers.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between rounded-lg border p-3 gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{m.email ?? m.user_id}</p>
                        {m.full_name && <p className="text-xs text-muted-foreground truncate">{m.full_name}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-[10px]">{m.role}</Badge>
                          <Badge
                            variant={m.permission_role === 'owner' ? 'default' : 'secondary'}
                            className={`text-[10px] ${m.permission_role === 'owner' ? 'bg-wt-green-primary/20 text-wt-green-primary' : ''}`}
                          >
                            {m.permission_role}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {m.user_id !== user?.id && (
                          <>
                            <Select
                              value={m.permission_role}
                              onValueChange={(v) => handleChangeMemberRole(m.user_id, v)}
                              disabled={updatingMemberId === m.user_id}
                            >
                              <SelectTrigger className="w-[100px] h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="owner">Owner</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10 h-8 px-2"
                              onClick={() => handleRemoveMember(m.user_id)}
                              disabled={removingMemberId === m.user_id}
                            >
                              {removingMemberId === m.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Remove'}
                            </Button>
                          </>
                        )}
                        {m.user_id === user?.id && (
                          <span className="text-xs text-muted-foreground">You</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invite */}
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5" />
                Invite User
              </CardTitle>
              <CardDescription>Generate a link for a user to sign up. They will be added to your org with the selected role.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email (optional — pre-fills signup form)</Label>
                <Input id="invite-email" type="email" placeholder="user@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="max-w-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4 max-w-sm">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homeowner">Homeowner</SelectItem>
                      <SelectItem value="installer">Installer</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Permission</Label>
                  <Select value={invitePermRole} onValueChange={setInvitePermRole}>
                    <SelectTrigger className="text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {inviteRole === 'homeowner' && (
                <div className="space-y-2">
                  <Label>Assign properties</Label>
                  <p className="text-xs text-muted-foreground">Select which properties this homeowner can access.</p>
                  <div className="max-h-48 overflow-y-auto space-y-1 rounded border p-2">
                    {properties.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No properties available.</p>
                    ) : (
                      properties.map((prop) => (
                        <label key={prop.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50 cursor-pointer">
                          <Checkbox
                            checked={invitePropertyIds.includes(prop.id)}
                            onCheckedChange={(checked) => {
                              setInvitePropertyIds((prev) =>
                                checked
                                  ? [...prev, prop.id]
                                  : prev.filter((id) => id !== prop.id)
                              );
                            }}
                          />
                          <span className="text-sm">{prop.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {invitePropertyIds.length > 0 && (
                    <p className="text-xs text-muted-foreground">{invitePropertyIds.length} selected</p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={handleCreateInvite} disabled={inviteLoading || (inviteRole === 'homeowner' && invitePropertyIds.length === 0)}>
                  {inviteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Generate invite link
                </Button>
                {inviteUrl && (
                  <Button variant="outline" size="sm" onClick={handleCopyInviteUrl}>
                    <Copy className="w-4 h-4 mr-1.5" />
                    Copy link
                  </Button>
                )}
              </div>
              {inviteUrl && <p className="text-sm text-muted-foreground break-all font-mono bg-muted/50 p-2 rounded">{inviteUrl}</p>}
            </CardContent>
          </Card>

          {/* Pending signups */}
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Pending signups
              </CardTitle>
              <CardDescription>Approve new users before they can sign in.</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingLoading ? (
                <div className="flex justify-center h-[200px] items-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : pending.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground">
                  <UserPlus className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">No pending signups</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pending.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between rounded-lg border p-4">
                      <div>
                        <p className="font-medium">{u.email ?? u.user_id}</p>
                        <p className="text-xs text-muted-foreground">Signed up {u.created_at ? new Date(u.created_at).toLocaleString() : ''}</p>
                      </div>
                      <Button size="sm" onClick={() => handleApprove(u.user_id)} disabled={approvingId === u.user_id}>
                        {approvingId === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        Approve
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. Properties */}
        <TabsContent value="properties">
          <p className="text-sm text-wt-text-secondary mb-4">
            Configure property details, battery, systems, and per-property notification preferences for each property.
          </p>
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Properties
              </CardTitle>
              <CardDescription>Open a property&apos;s settings to edit details, battery config, systems, and notifications for that property.</CardDescription>
            </CardHeader>
            <CardContent>
              {properties.length === 0 ? (
                <p className="text-sm text-muted-foreground">No properties yet. Add one via onboarding.</p>
              ) : (
                <ul className="space-y-2">
                  {properties.map((prop) => (
                    <li key={prop.id} className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <p className="font-medium">{prop.name}</p>
                        {prop.address && <p className="text-xs text-muted-foreground truncate max-w-[240px]">{prop.address}</p>}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/app/property/${prop.id}/settings`)}>
                        <Settings className="w-4 h-4 mr-1.5" />
                        Settings
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 6. Data & Billing */}
        <TabsContent value="data-billing" className="space-y-6">
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5" />
                Import history (backfill)
              </CardTitle>
              <CardDescription>Start a 30-day backfill for all properties. Track progress in History.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {properties.length} propert{properties.length === 1 ? 'y' : 'ies'} · {properties.filter((p) => plants.some((pl) => pl.property_id === p.id)).length} with plants
              </p>
              <Button onClick={handleStartBackfillForAll} disabled={backfillAllLoading || properties.length === 0}>
                {backfillAllLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Start backfill for all properties
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Billing
              </CardTitle>
              <CardDescription>Subscription and invoices.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {billingLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
              ) : canSee && tierSummary && billingPreview ? (
                <>
                  <p className="text-sm text-wt-text-primary">
                    {tierSummary.tiers.monitor} Monitor · {tierSummary.tiers.alert} Alert · {tierSummary.tiers.predict} Predict
                    <span className="font-mono ml-2">= ${billingPreview.monthly_total}/mo</span>
                  </p>
                  <Button variant="outline" asChild>
                    <Link to="/app/billing">
                      Manage billing and invoices
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">No active subscription</p>
                  <Button variant="outline" asChild>
                    <Link to="/app/billing">Go to Billing</Link>
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 7. Service Plans */}
        <TabsContent value="service-plans">
          <ServicePlansSection />
        </TabsContent>
      </Tabs>

      {/* Change password modal */}
      {passwordModal && (
        <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>Enter your new password. You will stay signed in.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label>Confirm password</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPasswordModal(false)}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={passwordSaving || !newPassword || newPassword !== confirmPassword}>
                {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update password
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
